/**
 * Locale-aware number and month formatting for the charts.
 *
 * The charts formatted numbers with `.toFixed()`, which always emits an English
 * decimal point: a Dutch reader saw "0.1234" where the convention is "0,1234".
 * Month names were worse — hardcoded English arrays in six files, so a Dutch UI
 * showed "Mar", "May" and "Oct", which are not Dutch months at all.
 *
 * Everything here takes the ACTIVE language rather than assuming one, so the
 * charts re-render in the reader's own convention when the language is switched.
 *
 * `utils/formatNumber` is locale-aware too and reuses `intlLocale` from here.
 * The split is by shape, not by locale: that module formats a plain number for
 * text, this one adds axis abbreviation and month names for charts.
 */

/** i18next is configured with these three; anything else falls back like it does. */
const LOCALES: Record<string, string> = {
  nl: 'nl-NL',
  en: 'en-GB',
  de: 'de-DE',
};

const FALLBACK = LOCALES.nl;

/**
 * Full locale for an i18next language tag.
 *
 * Strips a region before matching: i18n runs `load: "languageOnly"` so it
 * normally hands over a bare "en", but navigator detection can produce "en-US"
 * before that resolves and that must not drop to the Dutch fallback.
 */
export function intlLocale(lng: string | undefined | null): string {
  if (!lng) return FALLBACK;
  return LOCALES[lng.split('-')[0].toLowerCase()] ?? FALLBACK;
}

/**
 * Fixed-precision number in the active locale's convention.
 *
 * This is also what the ECharts tooltips must be given explicitly. A chart with no
 * `formatter` and no `valueFormatter` falls through to echarts' OWN value
 * rendering — `addCommas` in its util/format.ts, which splits on "." and joins
 * with "," and takes no locale at all. Six charts did that, so they showed an
 * English "1,234,567.8" in the tooltip beside a Dutch "1,2M" on their own axis.
 * There is no global echarts setting for it: every tooltip has to pass one.
 */
export function formatDecimal(
  value: number | null | undefined,
  decimals: number,
  lng: string | undefined
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return value.toLocaleString(intlLocale(lng), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Axis-label abbreviation: 1.500.000 -> "1,5M" in Dutch, "1.5M" in English.
 *
 * K and M are kept rather than Intl's `notation: 'compact'`, which renders
 * "1,5 mln" in Dutch — correct, but far wider than an axis tick allows.
 *
 * Switches on MAGNITUDE so negatives abbreviate too; a y-axis crossing zero was
 * otherwise left with unabbreviated negative ticks beside abbreviated positives.
 */
export function formatCompact(
  value: number | null | undefined,
  lng: string | undefined
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000) return `${formatDecimal(value / 1_000_000, 1, lng)}M`;
  if (magnitude >= 1_000) return `${formatDecimal(value / 1_000, 1, lng)}K`;
  return formatDecimal(Math.round(value), 0, lng);
}

/**
 * The twelve month abbreviations, January first, in the active language.
 *
 * Built from Intl rather than a literal table so adding a locale to i18n needs
 * no change here. Day 2 of the month on purpose: day 1 combined with a UTC
 * offset can roll back into the previous month in some zones.
 */
export function monthAbbreviations(lng: string | undefined): string[] {
  const fmt = new Intl.DateTimeFormat(intlLocale(lng), { month: 'short' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2021, i, 2)));
}
